// Shared UI components from DESIGN_SYSTEM.md §6.

import { h } from "./dom";
import { fmtEstimate, fmtRange, fmtSGD } from "../signals/timing";
import { confidenceOf } from "../signals/confidence";

/**
 * ValueRange — the P10–P90 band as a bar, the P50 as a tick, and an optional
 * marker (asking price, budget). Uncertainty is the content, not a footnote
 * (UX_FINDINGS U-04). Text carries every number, so the bar is decorative for
 * screen readers.
 */
export function valueRange(o: {
  p10: number; p50: number; p90: number;
  marker?: { value: number; label: string };
  mapeOos?: number | null; n: number;
  title?: string;
}): HTMLElement {
  const vals = [o.p10, o.p90, o.p50, ...(o.marker ? [o.marker.value] : [])];
  const lo = Math.min(...vals), hi = Math.max(...vals);
  const pad = (hi - lo) * 0.12 || hi * 0.05;
  const min = lo - pad, max = hi + pad;
  const pos = (v: number) => `${(((v - min) / (max - min)) * 100).toFixed(2)}%`;
  const conf = confidenceOf(o.mapeOos, o.n);
  const bar = h("div", { class: "vr-track", "aria-hidden": "true" },
    h("div", { class: "vr-band", style: `left:${pos(o.p10)};right:calc(100% - ${pos(o.p90)})` }),
    h("div", { class: "vr-p50", style: `left:${pos(o.p50)}` }),
    o.marker ? h("div", { class: "vr-marker", style: `left:${pos(o.marker.value)}` }) : null,
  );
  return h("div", { class: "value-range" },
    o.title ? h("div", { class: "stat-label" }, o.title) : null,
    h("div", { class: "vr-head" },
      h("span", { class: "stat-value" }, fmtEstimate(o.p50)),
      h("span", { class: `confidence conf-${conf.level}` }, conf.label)),
    h("div", { class: "vr-range" }, `Comparable units sell for ${fmtRange(o.p10, o.p90)}`),
    bar,
    h("div", { class: "vr-legend small muted" },
      h("span", {}, h("span", { class: "vr-key band", "aria-hidden": "true" }), "usual range (P10–P90)"),
      h("span", {}, h("span", { class: "vr-key p50", "aria-hidden": "true" }), "fair value (P50)"),
      o.marker ? h("span", {}, h("span", { class: "vr-key marker", "aria-hidden": "true" }), `${o.marker.label} ${fmtSGD(o.marker.value)}`) : null),
    h("div", { class: "small muted" }, conf.reason),
  );
}
